'use client';

import * as React from 'react';

/**
 * Root error boundary. Must stay lightweight: avoid importing app-wide providers
 * or heavy packages (monitoring/Sentry, etc.) — those pull a large graph and
 * have caused Turbopack HMR failures ("module factory is not available") for
 * this special file. Regular errors still go through `app/error.tsx`, which
 * reports to monitoring.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset?: () => void;
}): React.JSX.Element {
  React.useEffect(() => {
    console.error('[global-error]', error);
  }, [error]);

  return (
    <html lang="en">
      <body className="min-h-screen bg-white text-gray-900 dark:bg-gray-950 dark:text-gray-100">
        <div className="mx-auto flex min-h-screen max-w-2xl flex-col justify-center px-6 py-16">
          <div className="space-y-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Something went wrong
            </p>
            <h1 className="text-2xl font-semibold">We hit an unexpected error.</h1>
            <p className="text-sm text-muted-foreground">
              Try again. If this keeps happening, refresh the page.
              {error?.digest ? ` (Error ID: ${error.digest})` : ''}
            </p>
          </div>

          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            {reset ? (
              <button
                type="button"
                onClick={() => reset()}
                className="inline-flex h-10 items-center justify-center rounded-md bg-gray-900 px-4 text-sm font-medium text-white hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-200"
              >
                Try again
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex h-10 items-center justify-center rounded-md border border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-100 dark:hover:bg-gray-900"
            >
              Refresh page
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
